export { CATALOG_FILTER_EXISTS } from '@backstage/catalog-client';
import { createApiRef, createRouteRef, useRouteRefParams, useApi, errorApiRef, identityApiRef, storageApiRef, alertApiRef, configApiRef } from '@backstage/core-plugin-api';
import React, { createContext, useEffect, useState, useCallback, useMemo, useContext, forwardRef, useRef, Fragment } from 'react';
import { Alert, Autocomplete } from '@material-ui/lab';
import { createVersionedContext, createVersionedValueMap, useVersionedContext } from '@backstage/version-bridge';
import { useNavigate, generatePath } from 'react-router';
import { useAsyncRetry, useMountedState, useAsyncFn, useDebounce, useAsync, useObservable } from 'react-use';
import { ENTITY_DEFAULT_NAMESPACE, VIEW_URL_ANNOTATION, EDIT_URL_ANNOTATION, SOURCE_LOCATION_ANNOTATION, parseLocationReference, RELATION_MEMBER_OF, getEntityName, stringifyEntityRef, RELATION_OWNED_BY, serializeEntityRef, parseEntityRef, RELATION_PART_OF, ORIGIN_LOCATION_ANNOTATION } from '@backstage/catalog-model';
import { compact, isEqual, groupBy, chunk } from 'lodash';
import qs from 'qs';
import { Link, OverflowTooltip, Table, Select, Progress, ResponseErrorPanel } from '@backstage/core-components';
import jwtDecoder from 'jwt-decode';
import { Box, Typography, FormControlLabel, Checkbox, TextField, makeStyles, Toolbar, FormControl, Input, InputAdornment, IconButton, withStyles, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button, DialogContentText, Divider, Card, List, MenuItem, ListItemIcon, ListItemText, ListItemSecondaryAction } from '@material-ui/core';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Clear from '@material-ui/icons/Clear';
import Search from '@material-ui/icons/Search';
import capitalize from 'lodash/capitalize';
import StarBorder from '@material-ui/icons/StarBorder';
import Star from '@material-ui/icons/Star';
import Alert$1 from '@material-ui/lab/Alert';
import SettingsIcon from '@material-ui/icons/Settings';

const catalogApiRef = createApiRef({
  id: "plugin.catalog.service"
});

const NoIcon = () => null;
const rootRoute = createRouteRef({
  icon: NoIcon,
  path: "",
  title: "Catalog"
});
const catalogRouteRef = rootRoute;
const entityRoute = createRouteRef({
  icon: NoIcon,
  path: ":namespace/:kind/:name/*",
  title: "Entity",
  params: ["namespace", "kind", "name"]
});
const entityRouteRef = entityRoute;
function entityRouteParams(entity) {
  var _a, _b;
  return {
    kind: entity.kind.toLowerCase(),
    namespace: (_b = (_a = entity.metadata.namespace) == null ? void 0 : _a.toLowerCase()) != null ? _b : ENTITY_DEFAULT_NAMESPACE,
    name: entity.metadata.name
  };
}

const useEntityCompoundName = () => {
  const {kind, namespace, name} = useRouteRefParams(entityRouteRef);
  return {kind, namespace, name};
};

const EntityContext = createContext({
  entity: void 0,
  loading: true,
  error: void 0,
  refresh: () => {
  }
});
const OldEntityProvider = EntityContext.Provider;
const NewEntityContext = createVersionedContext("entity-context");
const AsyncEntityProvider = ({
  children,
  entity,
  loading,
  error,
  refresh
}) => {
  const value = {entity, loading, error, refresh};
  return /* @__PURE__ */ React.createElement(OldEntityProvider, {
    value
  }, /* @__PURE__ */ React.createElement(NewEntityContext.Provider, {
    value: createVersionedValueMap({1: value})
  }, children));
};
const EntityProvider = ({entity, children}) => /* @__PURE__ */ React.createElement(AsyncEntityProvider, {
  entity,
  loading: !Boolean(entity),
  error: void 0,
  refresh: void 0,
  children
});
const CompatibilityProvider = ({
  value,
  children
}) => {
  return /* @__PURE__ */ React.createElement(AsyncEntityProvider, {
    ...value,
    children
  });
};
EntityContext.Provider = CompatibilityProvider;
const useEntityFromUrl = () => {
  const {kind, namespace, name} = useEntityCompoundName();
  const navigate = useNavigate();
  const errorApi = useApi(errorApiRef);
  const catalogApi = useApi(catalogApiRef);
  const {
    value: entity,
    error,
    loading,
    retry: refresh
  } = useAsyncRetry(() => catalogApi.getEntityByName({kind, namespace, name}), [catalogApi, kind, namespace, name]);
  useEffect(() => {
    if (!name) {
      errorApi.post(new Error("No name provided!"));
      navigate("/");
    }
  }, [errorApi, navigate, error, loading, entity, name]);
  return {entity, loading, error, refresh};
};
function useEntity() {
  const versionedHolder = useVersionedContext("entity-context");
  if (!versionedHolder) {
    return {
      entity: void 0,
      loading: true,
      error: void 0,
      refresh: () => {
      }
    };
  }
  const value = versionedHolder.atVersion(1);
  if (!value) {
    throw new Error("EntityContext v1 not available");
  }
  const {entity, loading, error, refresh} = value;
  return {entity, loading, error, refresh};
}

function reduceCatalogFilters(filters) {
  return filters.reduce((compoundFilter, filter) => {
    return {
      ...compoundFilter,
      ...filter.getCatalogFilters ? filter.getCatalogFilters() : {}
    };
  }, {});
}
function reduceEntityFilters(filters) {
  return (entity) => filters.every((filter) => !filter.filterEntity || filter.filterEntity(entity));
}

function getEntityMetadataViewUrl(entity) {
  var _a;
  return (_a = entity.metadata.annotations) == null ? void 0 : _a[VIEW_URL_ANNOTATION];
}
function getEntityMetadataEditUrl(entity) {
  var _a;
  return (_a = entity.metadata.annotations) == null ? void 0 : _a[EDIT_URL_ANNOTATION];
}

function getEntityRelations(entity, relationType, filter) {
  var _a, _b;
  let entityNames = ((_b = (_a = entity == null ? void 0 : entity.relations) == null ? void 0 : _a.filter((r) => r.type === relationType)) == null ? void 0 : _b.map((r) => r.target)) || [];
  if (filter == null ? void 0 : filter.kind) {
    entityNames = entityNames == null ? void 0 : entityNames.filter((e) => e.kind.toLowerCase() === filter.kind.toLowerCase());
  }
  return entityNames;
}

function getEntitySourceLocation(entity, scmIntegrationsApi) {
  var _a;
  const sourceLocation = (_a = entity.metadata.annotations) == null ? void 0 : _a[SOURCE_LOCATION_ANNOTATION];
  if (!sourceLocation) {
    return void 0;
  }
  try {
    const sourceLocationRef = parseLocationReference(sourceLocation);
    const integration = scmIntegrationsApi.byUrl(sourceLocationRef.target);
    return {
      locationTargetUrl: sourceLocationRef.target,
      integrationType: integration == null ? void 0 : integration.type
    };
  } catch {
    return void 0;
  }
}

function isOwnerOf(owner, owned) {
  const possibleOwners = new Set([
    ...getEntityRelations(owner, RELATION_MEMBER_OF, {kind: "group"}),
    ...owner ? [getEntityName(owner)] : []
  ].map(stringifyEntityRef));
  const owners = getEntityRelations(owned, RELATION_OWNED_BY).map(stringifyEntityRef);
  for (const ownerItem of owners) {
    if (possibleOwners.has(ownerItem)) {
      return true;
    }
  }
  return false;
}

const EntityListContext = createContext(void 0);
const EntityListProvider = ({
  children
}) => {
  const isMounted = useMountedState();
  const catalogApi = useApi(catalogApiRef);
  const [requestedFilters, setRequestedFilters] = useState({});
  const [outputState, setOutputState] = useState(() => {
    var _a;
    const query = qs.parse(window.location.search, {
      ignoreQueryPrefix: true
    });
    return {
      appliedFilters: {},
      entities: [],
      backendEntities: [],
      queryParameters: (_a = query.filters) != null ? _a : {}
    };
  });
  const [{loading, error}, refresh] = useAsyncFn(async () => {
    var _a;
    const compacted = compact(Object.values(requestedFilters));
    const entityFilter = reduceEntityFilters(compacted);
    const backendFilter = reduceCatalogFilters(compacted);
    const previousBackendFilter = reduceCatalogFilters(compact(Object.values(outputState.appliedFilters)));
    const queryParams = Object.keys(requestedFilters).reduce((params, key) => {
      const filter = requestedFilters[key];
      if (filter == null ? void 0 : filter.toQueryValue) {
        params[key] = filter.toQueryValue();
      }
      return params;
    }, {});
    if (!isEqual(previousBackendFilter, backendFilter)) {
      const response = await catalogApi.getEntities({
        filter: backendFilter
      });
      setOutputState({
        appliedFilters: requestedFilters,
        backendEntities: response.items,
        entities: response.items.filter(entityFilter),
        queryParameters: queryParams
      });
    } else {
      setOutputState({
        appliedFilters: requestedFilters,
        backendEntities: outputState.backendEntities,
        entities: outputState.backendEntities.filter(entityFilter),
        queryParameters: queryParams
      });
    }
    if (isMounted()) {
      const oldParams = qs.parse(window.location.search, {
        ignoreQueryPrefix: true
      });
      const newParams = qs.stringify({...oldParams, filters: queryParams}, {addQueryPrefix: true});
      const newUrl = `${window.location.pathname}${newParams}`;
      (_a = window.history) == null ? void 0 : _a.replaceState(null, document.title, newUrl);
    }
  }, [catalogApi, requestedFilters, outputState], {loading: true});
  useDebounce(refresh, 10, [requestedFilters]);
  const updateFilters = useCallback((update) => {
    setRequestedFilters((prevFilters) => {
      const newFilters = typeof update === "function" ? update(prevFilters) : update;
      return {...prevFilters, ...newFilters};
    });
  }, []);
  const value = useMemo(() => ({
    filters: outputState.appliedFilters,
    entities: outputState.entities,
    backendEntities: outputState.backendEntities,
    updateFilters,
    queryParameters: outputState.queryParameters,
    loading,
    error
  }), [outputState, updateFilters, loading, error]);
  return /* @__PURE__ */ React.createElement(EntityListContext.Provider, {
    value
  }, children);
};
function useEntityListProvider() {
  const context = useContext(EntityListContext);
  if (!context)
    throw new Error("useEntityListProvider must be used within EntityListProvider");
  return context;
}

function formatEntityRefTitle(entityRef, opts) {
  const defaultKind = opts == null ? void 0 : opts.defaultKind;
  let kind;
  let namespace;
  let name;
  if ("metadata" in entityRef) {
    kind = entityRef.kind;
    namespace = entityRef.metadata.namespace;
    name = entityRef.metadata.name;
  } else {
    kind = entityRef.kind;
    namespace = entityRef.namespace;
    name = entityRef.name;
  }
  if (namespace === ENTITY_DEFAULT_NAMESPACE) {
    namespace = void 0;
  }
  kind = kind.toLowerCase();
  return `${serializeEntityRef({
    kind: defaultKind && defaultKind.toLowerCase() === kind ? void 0 : kind,
    name,
    namespace
  })}`;
}

const EntityRefLink = forwardRef((props, ref) => {
  var _a;
  const {entityRef, defaultKind, children, ...linkProps} = props;
  let kind;
  let namespace;
  let name;
  if ("metadata" in entityRef) {
    kind = entityRef.kind;
    namespace = entityRef.metadata.namespace;
    name = entityRef.metadata.name;
  } else {
    kind = entityRef.kind;
    namespace = entityRef.namespace;
    name = entityRef.name;
  }
  kind = kind.toLocaleLowerCase("en-US");
  const routeParams = {
    kind,
    namespace: (_a = namespace == null ? void 0 : namespace.toLocaleLowerCase("en-US")) != null ? _a : ENTITY_DEFAULT_NAMESPACE,
    name
  };
  return /* @__PURE__ */ React.createElement(Link, {
    ...linkProps,
    ref,
    to: generatePath(`/catalog/${entityRoute.path}`, routeParams)
  }, children, !children && formatEntityRefTitle(entityRef, {defaultKind}));
});

const EntityRefLinks = ({
  entityRefs,
  defaultKind,
  ...linkProps
}) => /* @__PURE__ */ React.createElement(React.Fragment, null, entityRefs.map((r, i) => /* @__PURE__ */ React.createElement(React.Fragment, {
  key: i
}, i > 0 && ", ", /* @__PURE__ */ React.createElement(EntityRefLink, {
  ...linkProps,
  entityRef: r,
  defaultKind
}))));

class EntityKindFilter {
  constructor(value) {
    this.value = value;
  }
  getCatalogFilters() {
    return {kind: this.value};
  }
  toQueryValue() {
    return this.value;
  }
}
class EntityTypeFilter {
  constructor(value) {
    this.value = value;
  }
  getTypes() {
    return Array.isArray(this.value) ? this.value : [this.value];
  }
  getCatalogFilters() {
    return {"spec.type": this.getTypes()};
  }
  toQueryValue() {
    return this.getTypes();
  }
}
class EntityTagFilter {
  constructor(values) {
    this.values = values;
  }
  filterEntity(entity) {
    return this.values.every((v) => {
      var _a;
      return ((_a = entity.metadata.tags) != null ? _a : []).includes(v);
    });
  }
  toQueryValue() {
    return this.values;
  }
}
class EntityTextFilter {
  constructor(value) {
    this.value = value;
  }
  filterEntity(entity) {
    var _a;
    const upperCaseValue = this.value.toLocaleUpperCase("en-US");
    return entity.metadata.name.toLocaleUpperCase("en-US").includes(upperCaseValue) || `${entity.metadata.title}`.toLocaleUpperCase("en-US").includes(upperCaseValue) || ((_a = entity.metadata.tags) == null ? void 0 : _a.join("").toLocaleUpperCase("en-US").indexOf(upperCaseValue)) !== -1;
  }
}
class EntityOwnerFilter {
  constructor(values) {
    this.values = values;
  }
  filterEntity(entity) {
    return this.values.some((v) => getEntityRelations(entity, RELATION_OWNED_BY).some((o) => formatEntityRefTitle(o, {defaultKind: "group"}) === v));
  }
  toQueryValue() {
    return this.values;
  }
}
class EntityLifecycleFilter {
  constructor(values) {
    this.values = values;
  }
  filterEntity(entity) {
    return this.values.some((v) => {
      var _a;
      return ((_a = entity.spec) == null ? void 0 : _a.lifecycle) === v;
    });
  }
  toQueryValue() {
    return this.values;
  }
}
class UserListFilter {
  constructor(value, isOwnedEntity, isStarredEntity) {
    this.value = value;
    this.isOwnedEntity = isOwnedEntity;
    this.isStarredEntity = isStarredEntity;
  }
  filterEntity(entity) {
    switch (this.value) {
      case "owned":
        return this.isOwnedEntity(entity);
      case "starred":
        return this.isStarredEntity(entity);
      default:
        return true;
    }
  }
  toQueryValue() {
    return this.value;
  }
}

function useEntityTypeFilter() {
  var _a;
  const catalogApi = useApi(catalogApiRef);
  const {
    filters: {kind: kindFilter, type: typeFilter},
    queryParameters,
    updateFilters
  } = useEntityListProvider();
  const queryParamTypes = [queryParameters.type].flat().filter(Boolean);
  const [selectedTypes, setSelectedTypes] = useState(queryParamTypes.length ? queryParamTypes : (_a = typeFilter == null ? void 0 : typeFilter.getTypes()) != null ? _a : []);
  const [availableTypes, setAvailableTypes] = useState([]);
  const kind = useMemo(() => kindFilter == null ? void 0 : kindFilter.value, [kindFilter]);
  const {
    error,
    loading,
    value: entities
  } = useAsync(async () => {
    if (kind) {
      const items = await catalogApi.getEntities({
        filter: {kind},
        fields: ["spec.type"]
      }).then((response) => response.items);
      return items;
    }
    return [];
  }, [kind, catalogApi]);
  const entitiesRef = useRef(entities);
  useEffect(() => {
    const oldEntities = entitiesRef.current;
    entitiesRef.current = entities;
    if (loading || !kind || oldEntities === entities) {
      return;
    }
    if (!entities)
      return;
    const countByType = entities.reduce((acc, entity) => {
      var _a2;
      if (typeof ((_a2 = entity.spec) == null ? void 0 : _a2.type) !== "string")
        return acc;
      const entityType = entity.spec.type.toLocaleLowerCase("en-US");
      if (!acc[entityType]) {
        acc[entityType] = 0;
      }
      acc[entityType] += 1;
      return acc;
    }, {});
    const newTypes = Object.entries(countByType).sort(([, count1], [, count2]) => count2 - count1).map(([type]) => type);
    setAvailableTypes(newTypes);
    const stillValidTypes = selectedTypes.filter((value) => newTypes.includes(value));
    setSelectedTypes(stillValidTypes);
  }, [loading, kind, selectedTypes, setSelectedTypes, entities]);
  useEffect(() => {
    updateFilters({
      type: selectedTypes.length ? new EntityTypeFilter(selectedTypes) : void 0
    });
  }, [selectedTypes, updateFilters]);
  return {
    loading,
    error,
    availableTypes,
    selectedTypes,
    setSelectedTypes
  };
}

function useEntityKinds() {
  const catalogApi = useApi(catalogApiRef);
  const {
    error,
    loading,
    value: kinds
  } = useAsync(async () => {
    const entities = await catalogApi.getEntities({fields: ["kind"]}).then((response) => response.items);
    return [...new Set(entities.map((e) => e.kind))].sort();
  });
  return {error, loading, kinds};
}

function useOwnUser() {
  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);
  return useAsync(() => catalogApi.getEntityByName({
    kind: "User",
    namespace: "default",
    name: identityApi.getUserId()
  }), [catalogApi, identityApi]);
}

const BATCH_SIZE = 20;
function useRelatedEntities(entity, {type, kind}) {
  const catalogApi = useApi(catalogApiRef);
  const {
    loading,
    value: entities,
    error
  } = useAsync(async () => {
    const relations = entity.relations && entity.relations.filter((r) => (!type || r.type.toLowerCase() === type.toLowerCase()) && (!kind || r.target.kind.toLowerCase() === kind.toLowerCase()));
    if (!relations) {
      return [];
    }
    const relationsByKindAndNamespace = Object.values(groupBy(relations, ({target}) => {
      return `${target.kind}:${target.namespace}`.toLowerCase();
    }));
    const batchedRelationsByKindAndNamespace = [];
    for (const rs of relationsByKindAndNamespace) {
      batchedRelationsByKindAndNamespace.push({
        kind: rs[0].target.kind,
        namespace: rs[0].target.namespace,
        nameBatches: chunk(rs.map((r) => r.target.name), BATCH_SIZE)
      });
    }
    const results = await Promise.all(batchedRelationsByKindAndNamespace.flatMap((rs) => {
      return rs.nameBatches.map((names) => {
        return catalogApi.getEntities({
          filter: {
            kind: rs.kind,
            "metadata.namespace": rs.namespace,
            "metadata.name": names
          }
        });
      });
    }));
    return results.flatMap((r) => r.items);
  }, [entity, type]);
  return {
    entities,
    loading,
    error
  };
}

const buildEntityKey = (component) => {
  var _a;
  return `entity:${component.kind}:${(_a = component.metadata.namespace) != null ? _a : "default"}:${component.metadata.name}`;
};
const useStarredEntities = () => {
  var _a;
  const storageApi = useApi(storageApiRef);
  const settingsStore = storageApi.forBucket("settings");
  const rawStarredEntityKeys = (_a = settingsStore.get("starredEntities")) != null ? _a : [];
  const [starredEntities, setStarredEntities] = useState(new Set(rawStarredEntityKeys));
  const observedItems = useObservable(settingsStore.observe$("starredEntities"));
  useEffect(() => {
    var _a2;
    if (observedItems == null ? void 0 : observedItems.newValue) {
      const currentValue = (_a2 = observedItems == null ? void 0 : observedItems.newValue) != null ? _a2 : [];
      setStarredEntities(new Set(currentValue));
    }
  }, [observedItems == null ? void 0 : observedItems.newValue]);
  const toggleStarredEntity = useCallback((entity) => {
    const entityKey = buildEntityKey(entity);
    if (starredEntities.has(entityKey)) {
      starredEntities.delete(entityKey);
    } else {
      starredEntities.add(entityKey);
    }
    settingsStore.set("starredEntities", Array.from(starredEntities));
  }, [starredEntities, settingsStore]);
  const isStarredEntity = useCallback((entity) => {
    const entityKey = buildEntityKey(entity);
    return starredEntities.has(entityKey);
  }, [starredEntities]);
  return {
    starredEntities,
    toggleStarredEntity,
    isStarredEntity
  };
};

function extendUserId(id) {
  try {
    const ref = parseEntityRef(id, {
      defaultKind: "User",
      defaultNamespace: "default"
    });
    return stringifyEntityRef(ref);
  } catch {
    return id;
  }
}
async function loadIdentityOwnerRefs(identityApi) {
  const id = identityApi.getUserId();
  const token = await identityApi.getIdToken();
  const result = [];
  if (id) {
    result.push(extendUserId(id));
  }
  if (token) {
    try {
      const decoded = jwtDecoder(token);
      if (decoded == null ? void 0 : decoded.ent) {
        [decoded.ent].flat().filter((x) => typeof x === "string").map((x) => x.toLocaleLowerCase("en-US")).forEach((x) => result.push(x));
      }
    } catch {
    }
  }
  return result;
}
async function loadCatalogOwnerRefs(catalogApi, identityOwnerRefs) {
  const result = new Array();
  const primaryUserRef = identityOwnerRefs.find((ref) => ref.startsWith("user:"));
  if (primaryUserRef) {
    const entity = await catalogApi.getEntityByName(parseEntityRef(primaryUserRef));
    if (entity) {
      const memberOf = getEntityRelations(entity, RELATION_MEMBER_OF, {
        kind: "Group"
      });
      for (const group of memberOf) {
        result.push(stringifyEntityRef(group));
      }
    }
  }
  return result;
}
function useEntityOwnership() {
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const {loading, value: refs} = useAsync(async () => {
    const identityRefs = await loadIdentityOwnerRefs(identityApi);
    const catalogRefs = await loadCatalogOwnerRefs(catalogApi, identityRefs);
    return new Set([...identityRefs, ...catalogRefs]);
  }, []);
  const isOwnedEntity = useMemo(() => {
    const myOwnerRefs = new Set(refs != null ? refs : []);
    return (entity) => {
      const entityOwnerRefs = ("metadata" in entity ? getEntityRelations(entity, RELATION_OWNED_BY) : [entity]).map(stringifyEntityRef);
      for (const ref of entityOwnerRefs) {
        if (myOwnerRefs.has(ref)) {
          return true;
        }
      }
      return false;
    };
  }, [refs]);
  return useMemo(() => ({loading, isOwnedEntity}), [loading, isOwnedEntity]);
}

const EntityKindPicker = ({
  initialFilter,
  hidden
}) => {
  var _a;
  const {updateFilters, queryParameters} = useEntityListProvider();
  const [selectedKind] = useState((_a = [queryParameters.kind].flat()[0]) != null ? _a : initialFilter);
  useEffect(() => {
    updateFilters({
      kind: selectedKind ? new EntityKindFilter(selectedKind) : void 0
    });
  }, [selectedKind, updateFilters]);
  if (hidden)
    return null;
  return /* @__PURE__ */ React.createElement(Alert, {
    severity: "warning"
  }, "Kind filter not yet available");
};

const icon$2 = /* @__PURE__ */ React.createElement(CheckBoxOutlineBlankIcon, {
  fontSize: "small"
});
const checkedIcon$2 = /* @__PURE__ */ React.createElement(CheckBoxIcon, {
  fontSize: "small"
});
const EntityLifecyclePicker = () => {
  var _a, _b;
  const {updateFilters, backendEntities, filters, queryParameters} = useEntityListProvider();
  const queryParamLifecycles = [queryParameters.lifecycles].flat().filter(Boolean);
  const [selectedLifecycles, setSelectedLifecycles] = useState(queryParamLifecycles.length ? queryParamLifecycles : (_b = (_a = filters.lifecycles) == null ? void 0 : _a.values) != null ? _b : []);
  useEffect(() => {
    updateFilters({
      lifecycles: selectedLifecycles.length ? new EntityLifecycleFilter(selectedLifecycles) : void 0
    });
  }, [selectedLifecycles, updateFilters]);
  const availableLifecycles = useMemo(() => [
    ...new Set(backendEntities.map((e) => {
      var _a2;
      return (_a2 = e.spec) == null ? void 0 : _a2.lifecycle;
    }).filter(Boolean))
  ].sort(), [backendEntities]);
  if (!availableLifecycles.length)
    return null;
  return /* @__PURE__ */ React.createElement(Box, {
    pb: 1,
    pt: 1
  }, /* @__PURE__ */ React.createElement(Typography, {
    variant: "button"
  }, "Lifecycle"), /* @__PURE__ */ React.createElement(Autocomplete, {
    "aria-label": "Lifecycle",
    multiple: true,
    options: availableLifecycles,
    value: selectedLifecycles,
    onChange: (_, value) => setSelectedLifecycles(value),
    renderOption: (option, {selected}) => /* @__PURE__ */ React.createElement(FormControlLabel, {
      control: /* @__PURE__ */ React.createElement(Checkbox, {
        icon: icon$2,
        checkedIcon: checkedIcon$2,
        checked: selected
      }),
      label: option
    }),
    size: "small",
    popupIcon: /* @__PURE__ */ React.createElement(ExpandMoreIcon, {
      "data-testid": "lifecycle-picker-expand"
    }),
    renderInput: (params) => /* @__PURE__ */ React.createElement(TextField, {
      ...params,
      variant: "outlined"
    })
  }));
};

const icon$1 = /* @__PURE__ */ React.createElement(CheckBoxOutlineBlankIcon, {
  fontSize: "small"
});
const checkedIcon$1 = /* @__PURE__ */ React.createElement(CheckBoxIcon, {
  fontSize: "small"
});
const EntityOwnerPicker = () => {
  var _a, _b;
  const {updateFilters, backendEntities, filters, queryParameters} = useEntityListProvider();
  const queryParamOwners = [queryParameters.owners].flat().filter(Boolean);
  const [selectedOwners, setSelectedOwners] = useState(queryParamOwners.length ? queryParamOwners : (_b = (_a = filters.owners) == null ? void 0 : _a.values) != null ? _b : []);
  useEffect(() => {
    updateFilters({
      owners: selectedOwners.length ? new EntityOwnerFilter(selectedOwners) : void 0
    });
  }, [selectedOwners, updateFilters]);
  const availableOwners = useMemo(() => [
    ...new Set(backendEntities.flatMap((e) => getEntityRelations(e, RELATION_OWNED_BY).map((o) => formatEntityRefTitle(o, {defaultKind: "group"}))).filter(Boolean))
  ].sort(), [backendEntities]);
  if (!availableOwners.length)
    return null;
  return /* @__PURE__ */ React.createElement(Box, {
    pb: 1,
    pt: 1
  }, /* @__PURE__ */ React.createElement(Typography, {
    variant: "button"
  }, "Owner"), /* @__PURE__ */ React.createElement(Autocomplete, {
    multiple: true,
    "aria-label": "Owner",
    options: availableOwners,
    value: selectedOwners,
    onChange: (_, value) => setSelectedOwners(value),
    renderOption: (option, {selected}) => /* @__PURE__ */ React.createElement(FormControlLabel, {
      control: /* @__PURE__ */ React.createElement(Checkbox, {
        icon: icon$1,
        checkedIcon: checkedIcon$1,
        checked: selected
      }),
      label: option
    }),
    size: "small",
    popupIcon: /* @__PURE__ */ React.createElement(ExpandMoreIcon, {
      "data-testid": "owner-picker-expand"
    }),
    renderInput: (params) => /* @__PURE__ */ React.createElement(TextField, {
      ...params,
      variant: "outlined"
    })
  }));
};

const useStyles$3 = makeStyles((_theme) => ({
  searchToolbar: {
    paddingLeft: 0,
    paddingRight: 0
  }
}));
const EntitySearchBar = () => {
  var _a, _b;
  const styles = useStyles$3();
  const {filters, updateFilters} = useEntityListProvider();
  const [search, setSearch] = useState((_b = (_a = filters.text) == null ? void 0 : _a.value) != null ? _b : "");
  useDebounce(() => {
    updateFilters({
      text: search.length ? new EntityTextFilter(search) : void 0
    });
  }, 250, [search, updateFilters]);
  return /* @__PURE__ */ React.createElement(Toolbar, {
    className: styles.searchToolbar
  }, /* @__PURE__ */ React.createElement(FormControl, null, /* @__PURE__ */ React.createElement(Input, {
    id: "input-with-icon-adornment",
    placeholder: "Search",
    autoComplete: "off",
    onChange: (event) => setSearch(event.target.value),
    value: search,
    startAdornment: /* @__PURE__ */ React.createElement(InputAdornment, {
      position: "start"
    }, /* @__PURE__ */ React.createElement(Search, null)),
    endAdornment: /* @__PURE__ */ React.createElement(InputAdornment, {
      position: "end"
    }, /* @__PURE__ */ React.createElement(IconButton, {
      "aria-label": "clear search",
      onClick: () => setSearch(""),
      edge: "end",
      disabled: search.length === 0
    }, /* @__PURE__ */ React.createElement(Clear, null)))
  })));
};

function createEntityRefColumn({
  defaultKind
}) {
  function formatContent(entity) {
    return formatEntityRefTitle(entity, {
      defaultKind
    });
  }
  return {
    title: "Name",
    highlight: true,
    customFilterAndSearch(filter, entity) {
      return formatContent(entity).includes(filter);
    },
    customSort(entity1, entity2) {
      return formatContent(entity1).localeCompare(formatContent(entity2));
    },
    render: (entity) => /* @__PURE__ */ React.createElement(EntityRefLink, {
      entityRef: entity,
      defaultKind
    })
  };
}
function createEntityRelationColumn({
  title,
  relation,
  defaultKind,
  filter: entityFilter
}) {
  function getRelations(entity) {
    return getEntityRelations(entity, relation, entityFilter);
  }
  function formatContent(entity) {
    return getRelations(entity).map((r) => formatEntityRefTitle(r, {defaultKind})).join(", ");
  }
  return {
    title,
    customFilterAndSearch(filter, entity) {
      return formatContent(entity).includes(filter);
    },
    customSort(entity1, entity2) {
      return formatContent(entity1).localeCompare(formatContent(entity2));
    },
    render: (entity) => {
      return /* @__PURE__ */ React.createElement(EntityRefLinks, {
        entityRefs: getRelations(entity),
        defaultKind
      });
    }
  };
}
function createOwnerColumn() {
  return createEntityRelationColumn({
    title: "Owner",
    relation: RELATION_OWNED_BY,
    defaultKind: "group"
  });
}
function createDomainColumn() {
  return createEntityRelationColumn({
    title: "Domain",
    relation: RELATION_PART_OF,
    defaultKind: "domain",
    filter: {
      kind: "domain"
    }
  });
}
function createSystemColumn() {
  return createEntityRelationColumn({
    title: "System",
    relation: RELATION_PART_OF,
    defaultKind: "system",
    filter: {
      kind: "system"
    }
  });
}
function createMetadataDescriptionColumn() {
  return {
    title: "Description",
    field: "metadata.description",
    render: (entity) => /* @__PURE__ */ React.createElement(OverflowTooltip, {
      text: entity.metadata.description,
      placement: "bottom-start",
      line: 2
    }),
    width: "auto"
  };
}
function createSpecLifecycleColumn() {
  return {
    title: "Lifecycle",
    field: "spec.lifecycle"
  };
}
function createSpecTypeColumn() {
  return {
    title: "Type",
    field: "spec.type"
  };
}

var columnFactories = /*#__PURE__*/Object.freeze({
  __proto__: null,
  createEntityRefColumn: createEntityRefColumn,
  createEntityRelationColumn: createEntityRelationColumn,
  createOwnerColumn: createOwnerColumn,
  createDomainColumn: createDomainColumn,
  createSystemColumn: createSystemColumn,
  createMetadataDescriptionColumn: createMetadataDescriptionColumn,
  createSpecLifecycleColumn: createSpecLifecycleColumn,
  createSpecTypeColumn: createSpecTypeColumn
});

const systemEntityColumns = [
  createEntityRefColumn({defaultKind: "system"}),
  createDomainColumn(),
  createOwnerColumn(),
  createMetadataDescriptionColumn()
];
const componentEntityColumns = [
  createEntityRefColumn({defaultKind: "component"}),
  createSystemColumn(),
  createOwnerColumn(),
  createSpecTypeColumn(),
  createSpecLifecycleColumn(),
  createMetadataDescriptionColumn()
];

const useStyles$2 = makeStyles((theme) => ({
  empty: {
    padding: theme.spacing(2),
    display: "flex",
    justifyContent: "center"
  }
}));
function EntityTable({
  entities,
  title,
  emptyContent,
  variant = "gridItem",
  columns
}) {
  const classes = useStyles$2();
  const tableStyle = {
    minWidth: "0",
    width: "100%"
  };
  if (variant === "gridItem") {
    tableStyle.height = "calc(100% - 10px)";
  }
  return /* @__PURE__ */ React.createElement(Table, {
    columns,
    title,
    style: tableStyle,
    emptyContent: emptyContent && /* @__PURE__ */ React.createElement("div", {
      className: classes.empty
    }, emptyContent),
    options: {
      search: false,
      paging: false,
      actionsColumnIndex: -1,
      padding: "dense"
    },
    data: entities
  });
}
EntityTable.columns = columnFactories;
EntityTable.systemEntityColumns = systemEntityColumns;
EntityTable.componentEntityColumns = componentEntityColumns;

const icon = /* @__PURE__ */ React.createElement(CheckBoxOutlineBlankIcon, {
  fontSize: "small"
});
const checkedIcon = /* @__PURE__ */ React.createElement(CheckBoxIcon, {
  fontSize: "small"
});
const EntityTagPicker = () => {
  var _a, _b;
  const {updateFilters, backendEntities, filters, queryParameters} = useEntityListProvider();
  const queryParamTags = [queryParameters.tags].flat().filter(Boolean);
  const [selectedTags, setSelectedTags] = useState(queryParamTags.length ? queryParamTags : (_b = (_a = filters.tags) == null ? void 0 : _a.values) != null ? _b : []);
  useEffect(() => {
    updateFilters({
      tags: selectedTags.length ? new EntityTagFilter(selectedTags) : void 0
    });
  }, [selectedTags, updateFilters]);
  const availableTags = useMemo(() => [
    ...new Set(backendEntities.flatMap((e) => e.metadata.tags).filter(Boolean))
  ].sort(), [backendEntities]);
  if (!availableTags.length)
    return null;
  return /* @__PURE__ */ React.createElement(Box, {
    pb: 1,
    pt: 1
  }, /* @__PURE__ */ React.createElement(Typography, {
    variant: "button"
  }, "Tags"), /* @__PURE__ */ React.createElement(Autocomplete, {
    multiple: true,
    "aria-label": "Tags",
    options: availableTags,
    value: selectedTags,
    onChange: (_, value) => setSelectedTags(value),
    renderOption: (option, {selected}) => /* @__PURE__ */ React.createElement(FormControlLabel, {
      control: /* @__PURE__ */ React.createElement(Checkbox, {
        icon,
        checkedIcon,
        checked: selected
      }),
      label: option
    }),
    size: "small",
    popupIcon: /* @__PURE__ */ React.createElement(ExpandMoreIcon, {
      "data-testid": "tag-picker-expand"
    }),
    renderInput: (params) => /* @__PURE__ */ React.createElement(TextField, {
      ...params,
      variant: "outlined"
    })
  }));
};

const EntityTypePicker = () => {
  var _a;
  const alertApi = useApi(alertApiRef);
  const {error, availableTypes, selectedTypes, setSelectedTypes} = useEntityTypeFilter();
  useEffect(() => {
    if (error) {
      alertApi.post({
        message: `Failed to load entity types`,
        severity: "error"
      });
    }
  }, [error, alertApi]);
  if (availableTypes.length === 0 || error)
    return null;
  const items = [
    {value: "all", label: "All"},
    ...availableTypes.map((type) => ({
      value: type,
      label: capitalize(type)
    }))
  ];
  return /* @__PURE__ */ React.createElement(Box, {
    pb: 1,
    pt: 1
  }, /* @__PURE__ */ React.createElement(Select, {
    label: "Type",
    items,
    selected: (_a = items.length > 1 ? selectedTypes[0] : void 0) != null ? _a : "all",
    onChange: (value) => setSelectedTypes(value === "all" ? [] : [String(value)])
  }));
};

const YellowStar = withStyles({
  root: {
    color: "#f3ba37"
  }
})(Star);
const favoriteEntityTooltip = (isStarred) => isStarred ? "Remove from favorites" : "Add to favorites";
const favoriteEntityIcon = (isStarred) => isStarred ? /* @__PURE__ */ React.createElement(YellowStar, null) : /* @__PURE__ */ React.createElement(StarBorder, null);
const FavoriteEntity = (props) => {
  const {toggleStarredEntity, isStarredEntity} = useStarredEntities();
  const isStarred = isStarredEntity(props.entity);
  return /* @__PURE__ */ React.createElement(IconButton, {
    color: "inherit",
    ...props,
    onClick: () => toggleStarredEntity(props.entity)
  }, /* @__PURE__ */ React.createElement(Tooltip, {
    title: favoriteEntityTooltip(isStarred)
  }, favoriteEntityIcon(isStarred)));
};

function useUnregisterEntityDialogState(entity) {
  var _a;
  const catalogApi = useApi(catalogApiRef);
  const locationRef = (_a = entity.metadata.annotations) == null ? void 0 : _a[ORIGIN_LOCATION_ANNOTATION];
  const uid = entity.metadata.uid;
  const isBootstrap = locationRef === "bootstrap:bootstrap";
  const prerequisites = useAsync(async () => {
    const locationPromise = catalogApi.getOriginLocationByEntity(entity);
    let colocatedEntitiesPromise;
    if (!locationRef) {
      colocatedEntitiesPromise = Promise.resolve([]);
    } else {
      const locationAnnotationFilter = `metadata.annotations.${ORIGIN_LOCATION_ANNOTATION}`;
      colocatedEntitiesPromise = catalogApi.getEntities({
        filter: {[locationAnnotationFilter]: locationRef},
        fields: [
          "kind",
          "metadata.uid",
          "metadata.name",
          "metadata.namespace"
        ]
      }).then((response) => response.items);
    }
    return Promise.all([locationPromise, colocatedEntitiesPromise]).then(([location2, colocatedEntities2]) => ({
      location: location2,
      colocatedEntities: colocatedEntities2
    }));
  }, [catalogApi, entity]);
  const unregisterLocation = useCallback(async function unregisterLocationFn() {
    const {location: location2, colocatedEntities: colocatedEntities2} = prerequisites.value;
    await catalogApi.removeLocationById(location2.id);
    await Promise.allSettled(colocatedEntities2.map((e) => catalogApi.removeEntityByUid(e.metadata.uid)));
  }, [catalogApi, prerequisites]);
  const deleteEntity = useCallback(async function deleteEntityFn() {
    await catalogApi.removeEntityByUid(uid);
  }, [catalogApi, uid]);
  if (isBootstrap) {
    return {type: "bootstrap", location: locationRef, deleteEntity};
  }
  const {loading, error, value} = prerequisites;
  if (loading) {
    return {type: "loading"};
  } else if (error) {
    return {type: "error", error};
  }
  const {location, colocatedEntities} = value;
  if (!location) {
    return {type: "only-delete", deleteEntity};
  }
  return {
    type: "unregister",
    location: locationRef,
    colocatedEntities: colocatedEntities.map(getEntityName),
    unregisterLocation,
    deleteEntity
  };
}

const useStyles$1 = makeStyles({
  advancedButton: {
    fontSize: "0.7em"
  }
});
const Contents = ({
  entity,
  onConfirm
}) => {
  var _a;
  const alertApi = useApi(alertApiRef);
  const configApi = useApi(configApiRef);
  const classes = useStyles$1();
  const state = useUnregisterEntityDialogState(entity);
  const [showDelete, setShowDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const appTitle = (_a = configApi.getOptionalString("app.title")) != null ? _a : "Backstage";
  const onUnregister = useCallback(async function onUnregisterFn() {
    if ("unregisterLocation" in state) {
      setBusy(true);
      try {
        await state.unregisterLocation();
        onConfirm();
      } catch (err) {
        alertApi.post({message: err.message});
      } finally {
        setBusy(false);
      }
    }
  }, [alertApi, onConfirm, state]);
  const onDelete = useCallback(async function onDeleteFn() {
    if ("deleteEntity" in state) {
      setBusy(true);
      try {
        await state.deleteEntity();
        onConfirm();
      } catch (err) {
        alertApi.post({message: err.message});
      } finally {
        setBusy(false);
      }
    }
  }, [alertApi, onConfirm, state]);
  if (state.type === "loading") {
    return /* @__PURE__ */ React.createElement(Progress, null);
  }
  if (state.type === "error") {
    return /* @__PURE__ */ React.createElement(ResponseErrorPanel, {
      error: state.error
    });
  }
  if (state.type === "bootstrap") {
    return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Alert$1, {
      severity: "info"
    }, 'You cannot unregister this entity, since it originates from a protected Backstage configuration (location "', state.location, '"). If you believe this is in error, please contact the ', appTitle, " ", "integrator."), /* @__PURE__ */ React.createElement(Box, {
      marginTop: 2
    }, !showDelete && /* @__PURE__ */ React.createElement(Button, {
      variant: "text",
      size: "small",
      color: "primary",
      className: classes.advancedButton,
      onClick: () => setShowDelete(true)
    }, "Advanced Options"), showDelete && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(DialogContentText, null, "You have the option to delete the entity itself from the catalog. Note that this should only be done if you know that the catalog file has been deleted at, or moved from, its origin location. If that is not the case, the entity will reappear shortly as the next refresh round is performed by the catalog."), /* @__PURE__ */ React.createElement(Button, {
      variant: "contained",
      color: "secondary",
      disabled: busy,
      onClick: onDelete
    }, "Delete Entity"))));
  }
  if (state.type === "only-delete") {
    return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(DialogContentText, null, "This entity does not seem to originate from a registered location. You therefore only have the option to delete it outright from the catalog."), /* @__PURE__ */ React.createElement(Button, {
      variant: "contained",
      color: "secondary",
      disabled: busy,
      onClick: onDelete
    }, "Delete Entity"));
  }
  if (state.type === "unregister") {
    return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(DialogContentText, null, "This action will unregister the following entities:"), /* @__PURE__ */ React.createElement(DialogContentText, {
      component: "ul"
    }, state.colocatedEntities.map((e) => /* @__PURE__ */ React.createElement("li", {
      key: `${e.kind}:${e.namespace}/${e.name}`
    }, /* @__PURE__ */ React.createElement(EntityRefLink, {
      entityRef: e
    })))), /* @__PURE__ */ React.createElement(DialogContentText, null, "Located at the following location:"), /* @__PURE__ */ React.createElement(DialogContentText, {
      component: "ul"
    }, /* @__PURE__ */ React.createElement("li", null, state.location)), /* @__PURE__ */ React.createElement(DialogContentText, null, "To undo, just re-register the entity in ", appTitle, "."), /* @__PURE__ */ React.createElement(Box, {
      marginTop: 2
    }, /* @__PURE__ */ React.createElement(Button, {
      variant: "contained",
      color: "secondary",
      disabled: busy,
      onClick: onUnregister
    }, "Unregister Location"), !showDelete && /* @__PURE__ */ React.createElement(Box, {
      component: "span",
      marginLeft: 2
    }, /* @__PURE__ */ React.createElement(Button, {
      variant: "text",
      size: "small",
      color: "primary",
      className: classes.advancedButton,
      onClick: () => setShowDelete(true)
    }, "Advanced Options"))), showDelete && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Box, {
      paddingTop: 4,
      paddingBottom: 4
    }, /* @__PURE__ */ React.createElement(Divider, null)), /* @__PURE__ */ React.createElement(DialogContentText, null, "You also have the option to delete the entity itself from the catalog. Note that this should only be done if you know that the catalog file has been deleted at, or moved from, its origin location. If that is not the case, the entity will reappear shortly as the next refresh round is performed by the catalog."), /* @__PURE__ */ React.createElement(Button, {
      variant: "contained",
      color: "secondary",
      disabled: busy,
      onClick: onDelete
    }, "Delete Entity")));
  }
  return /* @__PURE__ */ React.createElement(Alert$1, {
    severity: "error"
  }, "Internal error: Unknown state");
};
const UnregisterEntityDialog = ({
  open,
  onConfirm,
  onClose,
  entity
}) => /* @__PURE__ */ React.createElement(Dialog, {
  open,
  onClose
}, /* @__PURE__ */ React.createElement(DialogTitle, {
  id: "responsive-dialog-title"
}, "Are you sure you want to unregister this entity?"), /* @__PURE__ */ React.createElement(DialogContent, null, /* @__PURE__ */ React.createElement(Contents, {
  entity,
  onConfirm
})), /* @__PURE__ */ React.createElement(DialogActions, null, /* @__PURE__ */ React.createElement(Button, {
  onClick: onClose,
  color: "primary"
}, "Cancel")));

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: "rgba(0, 0, 0, .11)",
    boxShadow: "none",
    margin: theme.spacing(1, 0, 1, 0)
  },
  title: {
    margin: theme.spacing(1, 0, 0, 1),
    textTransform: "uppercase",
    fontSize: 12,
    fontWeight: "bold"
  },
  listIcon: {
    minWidth: 30,
    color: theme.palette.text.primary
  },
  menuItem: {
    minHeight: theme.spacing(6)
  },
  groupWrapper: {
    margin: theme.spacing(1, 1, 2, 1)
  }
}));
function getFilterGroups(orgName) {
  return [
    {
      name: "Personal",
      items: [
        {
          id: "owned",
          label: "Owned",
          icon: SettingsIcon
        },
        {
          id: "starred",
          label: "Starred",
          icon: Star
        }
      ]
    },
    {
      name: orgName != null ? orgName : "Company",
      items: [
        {
          id: "all",
          label: "All"
        }
      ]
    }
  ];
}
const UserListPicker = ({
  initialFilter,
  availableFilters
}) => {
  var _a, _b;
  const classes = useStyles();
  const configApi = useApi(configApiRef);
  const orgName = (_a = configApi.getOptionalString("organization.name")) != null ? _a : "Company";
  const filterGroups = getFilterGroups(orgName).map((filterGroup) => ({
    ...filterGroup,
    items: filterGroup.items.filter(({id}) => !availableFilters || availableFilters.includes(id))
  })).filter(({items}) => !!items.length);
  const {filters, updateFilters, backendEntities, queryParameters} = useEntityListProvider();
  const {isStarredEntity} = useStarredEntities();
  const {isOwnedEntity} = useEntityOwnership();
  const [selectedUserFilter, setSelectedUserFilter] = useState((_b = [queryParameters.user].flat()[0]) != null ? _b : initialFilter);
  const ownedFilter = useMemo(() => new UserListFilter("owned", isOwnedEntity, isStarredEntity), [isOwnedEntity, isStarredEntity]);
  const starredFilter = useMemo(() => new UserListFilter("starred", isOwnedEntity, isStarredEntity), [isOwnedEntity, isStarredEntity]);
  useEffect(() => {
    updateFilters({
      user: selectedUserFilter ? new UserListFilter(selectedUserFilter, isOwnedEntity, isStarredEntity) : void 0
    });
  }, [selectedUserFilter, isOwnedEntity, isStarredEntity, updateFilters]);
  const [entitiesWithoutUserFilter, setEntitiesWithoutUserFilter] = useState(backendEntities);
  useEffect(() => {
    const filterFn = reduceEntityFilters(compact(Object.values({...filters, user: void 0})));
    setEntitiesWithoutUserFilter(backendEntities.filter(filterFn));
  }, [filters, backendEntities]);
  function getFilterCount(id) {
    switch (id) {
      case "owned":
        return entitiesWithoutUserFilter.filter((entity) => ownedFilter.filterEntity(entity)).length;
      case "starred":
        return entitiesWithoutUserFilter.filter((entity) => starredFilter.filterEntity(entity)).length;
      default:
        return entitiesWithoutUserFilter.length;
    }
  }
  return /* @__PURE__ */ React.createElement(Card, {
    className: classes.root
  }, filterGroups.map((group) => /* @__PURE__ */ React.createElement(Fragment, {
    key: group.name
  }, /* @__PURE__ */ React.createElement(Typography, {
    variant: "subtitle2",
    className: classes.title
  }, group.name), /* @__PURE__ */ React.createElement(Card, {
    className: classes.groupWrapper
  }, /* @__PURE__ */ React.createElement(List, {
    disablePadding: true,
    dense: true
  }, group.items.map((item) => {
    var _a2, _b2;
    return /* @__PURE__ */ React.createElement(MenuItem, {
      key: item.id,
      button: true,
      divider: true,
      onClick: () => setSelectedUserFilter(item.id),
      selected: item.id === ((_a2 = filters.user) == null ? void 0 : _a2.value),
      className: classes.menuItem
    }, item.icon && /* @__PURE__ */ React.createElement(ListItemIcon, {
      className: classes.listIcon
    }, /* @__PURE__ */ React.createElement(item.icon, {
      fontSize: "small"
    })), /* @__PURE__ */ React.createElement(ListItemText, null, /* @__PURE__ */ React.createElement(Typography, {
      variant: "body1",
      "data-testid": `user-picker-${item.id}`
    }, item.label)), /* @__PURE__ */ React.createElement(ListItemSecondaryAction, null, (_b2 = getFilterCount(item.id)) != null ? _b2 : "-"));
  }))))));
};

const MockEntityListContextProvider = ({
  children,
  value
}) => {
  var _a;
  const [filters, setFilters] = useState((_a = value == null ? void 0 : value.filters) != null ? _a : {});
  const updateFilters = useCallback((update) => {
    setFilters((prevFilters) => {
      const newFilters = typeof update === "function" ? update(prevFilters) : update;
      return {...prevFilters, ...newFilters};
    });
  }, []);
  const defaultContext = {
    entities: [],
    backendEntities: [],
    updateFilters,
    filters,
    loading: false,
    queryParameters: {}
  };
  const {filters: _, ...otherContextFields} = value != null ? value : {};
  return /* @__PURE__ */ React.createElement(EntityListContext.Provider, {
    value: {...defaultContext, ...otherContextFields}
  }, children);
};

export { AsyncEntityProvider, EntityContext, EntityKindFilter, EntityKindPicker, EntityLifecycleFilter, EntityLifecyclePicker, EntityListContext, EntityListProvider, EntityOwnerFilter, EntityOwnerPicker, EntityProvider, EntityRefLink, EntityRefLinks, EntitySearchBar, EntityTable, EntityTagFilter, EntityTagPicker, EntityTextFilter, EntityTypeFilter, EntityTypePicker, FavoriteEntity, MockEntityListContextProvider, UnregisterEntityDialog, UserListFilter, UserListPicker, catalogApiRef, catalogRouteRef, entityRoute, entityRouteParams, entityRouteRef, favoriteEntityIcon, favoriteEntityTooltip, formatEntityRefTitle, getEntityMetadataEditUrl, getEntityMetadataViewUrl, getEntityRelations, getEntitySourceLocation, isOwnerOf, reduceCatalogFilters, reduceEntityFilters, rootRoute, useEntity, useEntityCompoundName, useEntityFromUrl, useEntityKinds, useEntityListProvider, useEntityOwnership, useEntityTypeFilter, useOwnUser, useRelatedEntities, useStarredEntities };
//# sourceMappingURL=index.esm.js.map
